import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { apiClient } from '../api/client';
import { LOAD_FAILURE_QUERY_OPTIONS } from '../lib/load-failure';
import type {
  TruckingInvoiceLineWrite, TruckingInvoiceLinesResponse, TruckingInvoiceRecipientResponse, TruckingInvoiceResponse,
  TruckingInvoiceSendResponse, TruckingInvoiceCreateResponse, TruckingInvoicesResponse,
  TruckingInvoiceVoidResponse,
} from '../types/trucking-invoices';

// The TruckingSettings interface and its defaultTruckingSettings / fleetTypes /
// erpOptions fixtures lived here to feed pages/TruckingSettings.tsx, which never
// called the API and is deleted. The live page is pages/settings/
// TruckingSettingsPage.tsx, typed to the trucking_settings row it actually
// reads. The default carried a hardcoded tenant name, which is not something to
// leave in the bundle behind a page nobody can reach.

// ── Real API hooks ──────────────────────────────────

export const useDrivers = () => useQuery({
  queryKey: ['trucking-drivers'],
  queryFn: () => apiClient.get('/trucking/drivers').then(r => r.data?.data ?? r.data ?? []),
});

export const useLoads = (filters?: { status?: string; date?: string }) => useQuery({
  queryKey: ['trucking-loads', filters],
  queryFn: () => apiClient.get('/trucking/loads', { params: filters }).then(r => r.data?.data ?? r.data ?? []),
});

export const useCreateLoad = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload: any) => apiClient.post('/trucking/loads', payload).then(r => r.data),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['trucking-loads'] }); },
  });
};

export const useUpdateLoadStatus = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ loadId, action }: { loadId: string; action: string }) =>
      apiClient.patch(`/trucking/loads/${loadId}/${action}`).then(r => r.data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['trucking-loads'] });
      qc.invalidateQueries({ queryKey: ['trucking-trucks'] });
    },
  });
};

export const useWeightTickets = () => useQuery({
  queryKey: ['trucking-weight-tickets'],
  queryFn: () => apiClient.get('/trucking/weight-tickets').then(r => r.data?.data ?? r.data ?? []),
});

export const useCreateWeightTicket = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload: any) => apiClient.post('/trucking/weight-tickets', payload).then(r => r.data),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['trucking-weight-tickets'] }); },
  });
};

// ── Move Jobs hooks ────────────────────────────────

export interface MoveJob {
  id: string;
  name: string;
  operator: string;
  operator_id?: string;
  rig_number?: string;
  origin_lsd: string;
  destination_lsd: string;
  planned_start: string;
  status: 'planned' | 'active' | 'complete';
  load_sequence_required: boolean;
  coordinator?: string;
  coordinator_id?: string;
  notes?: string;
  total_loads: number;
  signed_loads: number;
  master_bol_id?: string;
  master_bol_signed?: boolean;
  created_at: string;
  bols?: any[];
}

export const useMoveJobs = () => useQuery({
  queryKey: ['trucking-move-jobs'],
  queryFn: () => apiClient.get('/trucking/move-jobs').then(r => r.data?.data ?? r.data ?? []),
});

export const useMoveJob = (id: string) => useQuery({
  queryKey: ['trucking-move-job', id],
  queryFn: () => apiClient.get(`/trucking/move-jobs/${id}`).then(r => r.data?.data ?? r.data ?? null),
  enabled: !!id,
});

export const useCreateMoveJob = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload: any) => apiClient.post('/trucking/move-jobs', payload).then(r => r.data),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['trucking-move-jobs'] }); },
  });
};

export const useUpdateMoveJob = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, ...payload }: any) => apiClient.patch(`/trucking/move-jobs/${id}`, payload).then(r => r.data),
    onSuccess: (_d, vars) => {
      qc.invalidateQueries({ queryKey: ['trucking-move-jobs'] });
      qc.invalidateQueries({ queryKey: ['trucking-move-job', vars.id] });
    },
  });
};

export const useLinkBolToMoveJob = () => {
  const qc = useQueryClient();
  return useMutation({
    // The BOL id is a PATH segment, not a body field. Posting it in the body
    // hit `/move-jobs/:id/bols`, which the API does not register.
    mutationFn: ({ moveJobId, bolId }: { moveJobId: string; bolId: string }) =>
      apiClient.post(`/trucking/move-jobs/${moveJobId}/bols/${bolId}`).then(r => r.data),
    onSuccess: (_d, vars) => {
      qc.invalidateQueries({ queryKey: ['trucking-move-job', vars.moveJobId] });
      qc.invalidateQueries({ queryKey: ['trucking-move-jobs'] });
    },
  });
};

export const useGenerateMasterBol = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (moveJobId: string) =>
      apiClient.post(`/trucking/move-jobs/${moveJobId}/master-bol`).then(r => r.data),
    onSuccess: (_d, moveJobId) => {
      qc.invalidateQueries({ queryKey: ['trucking-move-job', moveJobId] });
    },
  });
};

export const useSignMasterBol = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ moveJobId, payload }: { moveJobId: string; payload: any }) =>
      apiClient.post(`/trucking/move-jobs/${moveJobId}/master-bol/sign`, payload).then(r => r.data),
    onSuccess: (_d, vars) => {
      qc.invalidateQueries({ queryKey: ['trucking-move-job', vars.moveJobId] });
      qc.invalidateQueries({ queryKey: ['trucking-move-jobs'] });
    },
  });
};

// ── Invoice hooks ─────────────────────────────────────
//
// The invoice list is Finance → Invoices (GET /unified-invoices, one list
// across verticals); a trucking row there opens /trucking/invoices/:id. These
// hooks are that page's: the stored invoice, who a send goes to (api#1006),
// the send (api#954: email / contact_name / cc, awaited), and a draft's lines.
// The untyped list, update and status hooks that stood here were imported by
// nothing and typed to fields the API never sent (qty, bol_ref, issued_date);
// useSendInvoice posted no body. Replaced, not kept beside the real ones.

export const truckingInvoiceKey = (id: string) => ['trucking-invoice', id] as const;

export const useTruckingInvoice = (id: string) => useQuery({
  queryKey: truckingInvoiceKey(id),
  queryFn: () => apiClient.get<TruckingInvoiceResponse>(`/trucking/invoices/${id}`).then((r) => r.data.data),
  enabled: !!id,
});

export const useTruckingInvoiceRecipient = (id: string, enabled: boolean) => useQuery({
  queryKey: ['trucking-invoice-recipient', id],
  queryFn: () => apiClient.get<TruckingInvoiceRecipientResponse>(`/trucking/invoices/${id}/recipient`).then((r) => r.data),
  enabled: !!id && enabled,
});

export const useSendTruckingInvoice = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, body }: { id: string; body: { email?: string; contact_name?: string; cc?: string[] } }) =>
      apiClient.post<TruckingInvoiceSendResponse>(`/trucking/invoices/${id}/send`, body).then((r) => r.data.data),
    onSuccess: (_d, vars) => {
      qc.invalidateQueries({ queryKey: truckingInvoiceKey(vars.id) });
      qc.invalidateQueries({ queryKey: ['trucking-invoice-recipient', vars.id] });
      // Finance → Invoices lists trucking rows from the unified list.
      qc.invalidateQueries({ queryKey: ['finance-invoices-other-verticals'] });
      qc.invalidateQueries({ queryKey: ['invoice-tab-counts'] });
    },
  });
};

/**
 * POST /trucking/invoices/:id/void `{ reason }` (api#1016). Void is final and
 * releases the invoice's BOLs, so every read that names an invoice for a BOL
 * or a move job is read again. Refusals are the caller's to show as sent.
 */
export const useVoidTruckingInvoice = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, reason }: { id: string; reason: string }) =>
      apiClient.post<TruckingInvoiceVoidResponse>(`/trucking/invoices/${id}/void`, { reason }).then((r) => r.data),
    onSuccess: (_d, vars) => {
      qc.invalidateQueries({ queryKey: truckingInvoiceKey(vars.id) });
      qc.invalidateQueries({ queryKey: truckingInvoicesKey });
      qc.invalidateQueries({ queryKey: ['trucking-bols'] });
      qc.invalidateQueries({ queryKey: ['bol-detail'] });
      qc.invalidateQueries({ queryKey: ['trucking-move-job'] });
      // Finance → Invoices lists trucking rows from the unified list.
      qc.invalidateQueries({ queryKey: ['finance-invoices-other-verticals'] });
      qc.invalidateQueries({ queryKey: ['invoice-tab-counts'] });
    },
  });
};

/** The API's PDF of the invoice, as a Blob. */
export const fetchTruckingInvoicePdf = (id: string) =>
  apiClient.get<Blob>(`/trucking/invoices/${id}/pdf`, { responseType: 'blob' }).then((r) => r.data);

export const useSaveTruckingInvoiceLines = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, lineItems }: { id: string; lineItems: TruckingInvoiceLineWrite[] }) =>
      apiClient.patch<TruckingInvoiceLinesResponse>(`/trucking/invoices/${id}/line-items`, { lineItems }).then((r) => r.data.data),
    onSuccess: (_d, vars) => {
      qc.invalidateQueries({ queryKey: truckingInvoiceKey(vars.id) });
      // Finance → Invoices lists trucking rows from the unified list.
      qc.invalidateQueries({ queryKey: ['finance-invoices-other-verticals'] });
      qc.invalidateQueries({ queryKey: ['invoice-tab-counts'] });
    },
  });
};

/**
 * Every trucking invoice the tenant has (the route is unpaginated). The move
 * job page reads it for the move job's invoice; a BOL's comes on the BOL
 * (`billed_on`, api#1011).
 * Read roles only (INVOICE_READ); a refusal is a refusal, not "none".
 */
export const truckingInvoicesKey = ['trucking-invoices'] as const;
export const useTruckingInvoices = (enabled: boolean) => useQuery({
  queryKey: truckingInvoicesKey,
  queryFn: () => apiClient.get<TruckingInvoicesResponse>('/trucking/invoices').then((r) => r.data.data),
  enabled,
  // A refusal is an answer (never retried); a dead zone is not "no invoice".
  ...LOAD_FAILURE_QUERY_OPTIONS,
});

const invalidateAfterCreate = (qc: ReturnType<typeof useQueryClient>) => {
  qc.invalidateQueries({ queryKey: truckingInvoicesKey });
  // Every BOL read carries its own coverage (api#1011): the list, and each BOL page.
  qc.invalidateQueries({ queryKey: ['trucking-bols'] });
  qc.invalidateQueries({ queryKey: ['bol-detail'] });
  // Finance → Invoices lists trucking rows from the unified list.
  qc.invalidateQueries({ queryKey: ['finance-invoices-other-verticals'] });
  qc.invalidateQueries({ queryKey: ['invoice-tab-counts'] });
};

/** POST /trucking/invoices/from-bol/:bolId. The refusal (400 / 404 / 409 / 422) is the caller's to show as sent. */
export const useCreateInvoiceFromBol = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (bolId: string) =>
      apiClient.post<TruckingInvoiceCreateResponse>(`/trucking/invoices/from-bol/${bolId}`).then((r) => r.data.data),
    onSuccess: () => {
      invalidateAfterCreate(qc);
    },
  });
};

/** POST /trucking/invoices/from-move-job/:moveJobId. A 422 names each BOL that cannot be priced. */
export const useCreateInvoiceFromMoveJob = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (moveJobId: string) =>
      apiClient.post<TruckingInvoiceCreateResponse>(`/trucking/invoices/from-move-job/${moveJobId}`).then((r) => r.data.data),
    onSuccess: (_d, moveJobId) => {
      invalidateAfterCreate(qc);
      qc.invalidateQueries({ queryKey: ['trucking-move-job', moveJobId] });
    },
  });
};

// `TruckLoad`, `apiLoadsToTruckLoads`, `useTruckingData` and `useTrucks` are gone
// (#815). Nothing imported them any more: the dispatch board and the calendar
// had stopped reading them, and `useTrucks` listed the legacy `trucks` table,
// which no load or weight ticket can point at since api#860 moved those
// foreign keys to `trucking_fleet`. A truck picker lists fleet units
// (`components/trucking/FleetUnitPicker.tsx`); the dispatch counts are the
// API's (`GET /trucking/dispatch/metrics`).
